import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { createDocBlob, getExportExtension } from './exportDocument';
import { getExportMime } from './nativeExport';

function toCellValue(value) {
  if (value === null || value === undefined) return '';
  return value;
}

function getPdfOrientation(headers, preferred = 'auto') {
  if (preferred === 'portrait' || preferred === 'landscape') return preferred;
  return headers.length > 6 ? 'landscape' : 'portrait';
}

function createPdfBlob({
  title,
  headers,
  rows,
  orientation = 'auto',
  meta = {},
}) {
  const doc = new jsPDF({
    orientation: getPdfOrientation(headers, orientation),
    unit: 'pt',
    format: 'a4',
  });

  const appName = meta.appName || 'NMhelper';
  const schoolName = meta.schoolName || '';
  const exportedAt = meta.exportedAt || new Date().toISOString().slice(0, 10);
  const subtitleParts = [
    schoolName ? `School: ${schoolName}` : '',
    `Exported: ${exportedAt}`,
  ].filter(Boolean);

  doc.setFontSize(12);
  doc.text(String(title || 'Export'), 40, 36);
  doc.setFontSize(9);
  if (subtitleParts.length) {
    doc.text(subtitleParts.join(' | '), 40, 50);
  }

  autoTable(doc, {
    startY: subtitleParts.length ? 60 : 48,
    head: [headers],
    body: (rows || []).map((row) => headers.map((h) => String(toCellValue(row?.[h])))),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [0, 80, 179] },
    margin: { left: 24, right: 24 },
    theme: 'grid',
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text(appName, 24, pageHeight - 14);
    doc.text(
      `Page ${page} / ${totalPages}`,
      pageWidth - 24,
      pageHeight - 14,
      { align: 'right' }
    );
  }

  return doc.output('blob');
}

export function createExportBlob({
  fileType,
  title,
  headers,
  rows,
  pdfOrientation = 'auto',
  pdfMeta = {},
}) {
  let blob;
  if (fileType === 'doc') {
    blob = createDocBlob(title, headers, rows);
  } else if (fileType === 'pdf') {
    blob = createPdfBlob({
      title,
      headers,
      rows,
      orientation: pdfOrientation,
      meta: pdfMeta,
    });
  } else {
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: headers,
      skipHeader: false,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    if (fileType === 'csv') {
      const csvText = XLSX.write(workbook, { bookType: 'csv', type: 'string' });
      blob = new Blob([csvText], { type: getExportMime(fileType) });
    } else {
      const fileData = XLSX.write(workbook, { bookType: 'ods', type: 'array' });
      blob = new Blob([fileData], { type: getExportMime(fileType) });
    }
  }

  return {
    blob,
    mimeType: getExportMime(fileType),
    extension: getExportExtension(fileType),
  };
}

function createNutritionAllPdfBlob({ title, dateIso, sections, pdfMeta = {} }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const appName = pdfMeta.appName || 'NMhelper';
  const schoolName = pdfMeta.schoolName || '';
  const exportedAt = pdfMeta.exportedAt || dateIso || new Date().toISOString().slice(0, 10);
  const subtitleParts = [
    schoolName ? `School: ${schoolName}` : '',
    `Exported: ${exportedAt}`,
  ].filter(Boolean);

  doc.setFontSize(12);
  doc.text(String(title || 'Nutrition export'), 40, 36);
  doc.setFontSize(9);
  if (subtitleParts.length) {
    doc.text(subtitleParts.join(' | '), 40, 50);
  }

  let startY = subtitleParts.length ? 60 : 48;
  sections.forEach((section, index) => {
    if (index > 0) startY += 8;
    doc.setFontSize(10);
    doc.text(String(section.sheetName || section.itemLabel || ''), 40, startY);
    startY += 6;
    autoTable(doc, {
      startY: startY + 8,
      head: [section.headers],
      body: (section.rows || []).map((row) =>
        section.headers.map((h) => String(toCellValue(row?.[h])))
      ),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [0, 80, 179] },
      margin: { left: 24, right: 24 },
      theme: 'grid',
    });
    startY = doc.lastAutoTable.finalY + 12;
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text(appName, 24, pageHeight - 14);
    doc.text(`Page ${page} / ${totalPages}`, pageWidth - 24, pageHeight - 14, { align: 'right' });
  }

  return doc.output('blob');
}

function combineNutritionSections(sections) {
  const itemHeader = sections[0]?.itemHeader || 'Item';
  const detailHeaders = sections[0]?.headers || [];
  const headers = [itemHeader, ...detailHeaders];
  const rows = sections.flatMap((section) =>
    (section.rows || []).map((row) => ({
      [itemHeader]: section.sheetName || section.itemLabel,
      ...row,
    }))
  );
  return { headers, rows };
}

/** One file with every nutrition item (egg, banana, milk) for a date. */
export function createNutritionAllExportBlob({
  fileType,
  title,
  dateIso,
  sections,
  pdfMeta = {},
}) {
  if (!sections?.length) {
    throw new Error('No nutrition sections to export');
  }

  if (fileType === 'pdf') {
    return {
      blob: createNutritionAllPdfBlob({ title, dateIso, sections, pdfMeta }),
      mimeType: getExportMime(fileType),
      extension: getExportExtension(fileType),
    };
  }

  if (fileType === 'doc') {
    const { headers, rows } = combineNutritionSections(sections);
    return {
      blob: createDocBlob(title, headers, rows),
      mimeType: getExportMime(fileType),
      extension: getExportExtension(fileType),
    };
  }

  const workbook = XLSX.utils.book_new();

  if (fileType === 'csv') {
    const { headers, rows } = combineNutritionSections(sections);
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: headers,
      skipHeader: false,
    });
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Nutrition');
    const csvText = XLSX.write(workbook, { bookType: 'csv', type: 'string' });
    return {
      blob: new Blob([csvText], { type: getExportMime(fileType) }),
      mimeType: getExportMime(fileType),
      extension: getExportExtension(fileType),
    };
  }

  sections.forEach((section) => {
    const sheetName = String(section.sheetName || section.itemLabel || 'Item').slice(0, 31);
    const worksheet = XLSX.utils.json_to_sheet(section.rows || [], {
      header: section.headers,
      skipHeader: false,
    });
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const fileData = XLSX.write(workbook, { bookType: 'ods', type: 'array' });
  return {
    blob: new Blob([fileData], { type: getExportMime(fileType) }),
    mimeType: getExportMime(fileType),
    extension: getExportExtension(fileType),
  };
}
